"use client";

import { useQueryClient } from "@tanstack/react-query";

import { useOpenApiMutation } from "../../../../request/hooks/openApi/useOpenApiMutation";
import { useOpenApiQuery } from "../../../../request/hooks/openApi/useOpenApiQuery";
import type { UseRequestQueryProps } from "../../../../request/hooks/useRequestQuery";
import { keyAdminDashUser } from "./keyAdminDashUser";
import { KEY_COMMUNITIES_ADMIN_USERS_BASE } from "./keyCommunitiesAdminUsers";

export type UseRequestAdminDashboardMemberProps = UseRequestQueryProps & {
  communityId: number;
  userId: number;
};

const urlBackendPath =
  "/communities/{community_id}/admin/users/{user_id}" as const;

export const useRequestAdminDashboardMember = ({
  communityId,
  userId,
  ...props
}: UseRequestAdminDashboardMemberProps) => {
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: keyAdminDashUser({ communityId, userId }),
    });

    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey[0] as string[];
        return (
          Array.isArray(queryKey) &&
          queryKey.length >= 2 &&
          queryKey[0] === "admin-dash-members" &&
          queryKey[1] === communityId.toString()
        );
      },
    });

    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey
          .flat()
          .some((keyPart) => keyPart === KEY_COMMUNITIES_ADMIN_USERS_BASE),
    });
  };

  return {
    query: useOpenApiQuery<typeof urlBackendPath>({
      ...props,
      urlBackendPath,
      params: { path: { community_id: communityId, user_id: userId } },
      queryKey: keyAdminDashUser({ communityId, userId }),
    }),
    mutationPut: useOpenApiMutation<"PUT", typeof urlBackendPath>({
      method: "PUT",
      urlBackendPath,
      onSuccess: invalidateQueries,
    }),
  };
};
